import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogContent,
    useMediaQuery,
    Theme,
    Button,
    DialogActions,
} from "@mui/material";
import Iconify from "../iconify";
import { format, isValid } from "date-fns";
import { DateRange, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

// Add custom styles using CSS-in-JS
const calendarStyles = `
    .custom-date-range {
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
        overflow: hidden;
    }
    .custom-date-range .rdrCalendarWrapper {
        width: 100%;
        max-width: 100%;
        font-size: 14px;
    }
    .custom-date-range .rdrMonthsHorizontal {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }
    .custom-date-range .rdrMonthAndYearWrapper {
        padding-top: 10px;
        width: 100%;
    }
    .custom-date-range .rdrMonth {
        width: 48% !important;
    }
    @media (max-width: 600px) {
        .custom-date-range .rdrMonth {
            width: 100% !important;
        }
    }
`;

interface CustomDateRangePickerProps {
    value: [Date | null, Date | null];
    onChange: (newValue: [Date | null, Date | null]) => void;
    label?: string;
    isMobile?: boolean;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
    value,
    onChange,
    label = "Date Range",
    isMobile = false,
}) => {
    const [open, setOpen] = useState(false);
    const [tempDateRange, setTempDateRange] = useState<
        [Date | null, Date | null]
    >([null, null]);
    const isSmallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("sm")
    );

    useEffect(() => {
        if (open) {
            setTempDateRange([value[0], value[1]]);
        }
    }, [open, value]);

    useEffect(() => {
        const styleEl = document.createElement("style");
        styleEl.innerHTML = calendarStyles;
        document.head.appendChild(styleEl);

        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    const formatDisplayValue = () => {
        if (!value[0] && !value[1]) return "";

        const fromDate =
            value[0] && isValid(value[0]) ? format(value[0], "dd/MM/yyyy") : "";
        const toDate =
            value[1] && isValid(value[1]) ? format(value[1], "dd/MM/yyyy") : "";

        return value[0] && value[1] ? `${fromDate} - ${toDate}` : fromDate;
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([null, null]);
    };

    const handleDateRangeChange = (item: RangeKeyDict) => {
        const { startDate, endDate } = item.selection;
        setTempDateRange([startDate || null, endDate || null]);
    };

    const handleApply = () => {
        onChange(tempDateRange);
        setOpen(false);
    };

    const dateRangeSelection = {
        startDate: tempDateRange[0] || value[0] || new Date(),
        endDate: tempDateRange[1] || value[1] || new Date(),
        key: "selection",
    };

    return (
        <Box
            sx={{
                width: 220,
            }}
            className={value[0] && value[1] ? "filled" : ""}
        >
            <TextField
                fullWidth
                placeholder={label}
                // size={"medium"}
                value={formatDisplayValue()}
                onClick={() => setOpen(true)}
                className="custom-date-range-picker"
                InputProps={{
                    readOnly: true,
                    sx: {
                        "&.MuiInputBase-root": {
                            "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.23) !important",
                            },
                            "&:hover fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.87) !important",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: (theme) =>
                                    `${theme.palette.primary.main} !important`,
                                borderWidth: "1px !important",
                            },
                            background: "white",
                            cursor: "pointer",
                            "&.MuiInputBase-readOnly": {
                                "& fieldset": {
                                    borderColor:
                                        "rgba(0, 0, 0, 0.23) !important",
                                },
                            },
                            height: "47px",
                        },
                    },

                    endAdornment: (
                        <InputAdornment position="end">
                            {value[0] && (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={handleClear}
                                    sx={{ mr: 0.5 }}
                                    aria-label="clear date range"
                                >
                                    <Iconify icon="material-symbols:close" />
                                </IconButton>
                            )}
                            <Iconify icon="material-symbols:calendar-month-outline" />
                        </InputAdornment>
                    ),
                }}
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        "& .MuiDialogContent-root": {
                            padding: 0,
                            overflow: "hidden",
                        },
                        borderRadius: "16px",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogContent>
                    <Box sx={{ width: "100%", overflowX: "hidden" }}>
                        <DateRange
                            ranges={[dateRangeSelection]}
                            onChange={handleDateRangeChange}
                            moveRangeOnFirstSelection={false}
                            months={isSmallScreen ? 1 : 2}
                            direction={
                                isSmallScreen ? "vertical" : "horizontal"
                            }
                            showDateDisplay={true}
                            rangeColors={["#3E3C47"]}
                            showMonthAndYearPickers={true}
                            showPreview={true}
                            dateDisplayFormat="dd/MM/yyyy"
                            className="custom-date-range"
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{ px: 3, py: 2, justifyContent: "flex-end" }}
                >
                    <Button
                        onClick={() => setOpen(false)}
                        variant="outlined"
                        color="inherit"
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        color="primary"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomDateRangePicker;
